import React, { useCallback, useEffect, useRef, useState } from 'react';
import { type DeepPartialSkipArrayKey, FormProvider, useForm } from 'react-hook-form';

import { type CreateCustomRoleRequest } from '@column/api';
import { Icon } from '@column/column-ui-kit';
import { Button } from '@column/ui';

import { buildCreateRequestForEntitiesAndAccountsSubset } from '../utils';
import { StateTabs } from '~/app/Layout';
import { PageActions } from '~/app/Layout/PageFrameContext';
import { RoleInfo } from '~/app/pages/Platform/Roles/_partials/RoleInfo';
import { ROUTE } from '~/app/routes';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { useCreateCustomRole, useUpdateCustomRole } from '~/hooks/useRoles';
import { useNavigate } from '~/lib/navigation';
import PermissionConfiguration, { Mode, PermissionConfigurationSection, useEntitiesAndAccounts } from '~/lib/roles';
import { RoleRepository } from '~/repositories/RoleRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';

enum Section {
  Info = 'role-info',
  Transfers = 'role-transfers-and-accounts',
  Additional = 'role-additional-permissions',
}

const sections = [
  { label: 'Role Info', value: Section.Info },
  { label: 'Transfers & Accounts', value: Section.Transfers },
  { label: 'Additional Permissions', value: Section.Additional },
];
const sectionValues = sections.map((section) => section.value);

const trimDefaults = (
  data: DeepPartialSkipArrayKey<KeysCamelCaseDeep<CreateCustomRoleRequest>>
): DeepPartialSkipArrayKey<KeysCamelCaseDeep<CreateCustomRoleRequest>> => {
  data.platformLevelPermissions = Object.fromEntries(
    Object.entries(data.platformLevelPermissions ?? {}).filter(([, v]) => v !== 'default')
  );
  data.entityLevelOverrides = (data.entityLevelOverrides ?? [])
    .map((entity) => {
      return Object.fromEntries(Object.entries(entity).filter(([k, v]) => k === 'entityId' || v !== 'default'));
    })
    .filter((entity) => Object.keys(entity).length > 1);
  data.bankAccountLevelOverrides = (data.bankAccountLevelOverrides ?? [])
    .map((bankAccount) => {
      return Object.fromEntries(
        Object.entries(bankAccount).filter(([k, v]) => k === 'bankAccountId' || v !== 'default')
      );
    })
    .filter((entity) => Object.keys(entity).length > 1);
  return data;
};

export const PageCreateRole: React.FC = () => {
  const { currentPlatform } = useSessionStore();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const createRole = useCreateCustomRole({
    onSuccess: () => {
      addSuccessNotification({ content: 'Role created successfully' });
      navigate(ROUTE.PLATFORM_ROLES);
    },
    onError: (error) => {
      addDangerNotification({ content: error.message });
    },
  });
  const updateRole = useUpdateCustomRole({
    onSuccess: (response) => {
      addSuccessNotification({
        content: response.pendingApproval
          ? 'Role created. Live permissions submitted for approval.'
          : 'Role created successfully',
      });
      navigate(ROUTE.PLATFORM_ROLES);
    },
    onError: (error) => {
      addDangerNotification({ content: error.message });
    },
  });
  const { handleSubmitErrors } = useFormErrorHandler();
  const sandboxEntitiesAndAccounts = useEntitiesAndAccounts({ mode: Mode.Sandbox });
  const liveEntitiesAndAccounts = useEntitiesAndAccounts({ mode: Mode.Live });

  const [visibleSection, setVisibleSection] = useState<Section>(Section.Info);
  const isLastSection = visibleSection === Section.Additional;
  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!sectionValues.includes(visibleSection)) {
      setVisibleSection(Section.Info);
    }
    // The footer Next button sits below long sections, so bring the next section's start into view.
    topRef.current?.scrollIntoView({ block: 'nearest' });
  }, [visibleSection]);

  const handleGoForward = useCallback(() => {
    setVisibleSection(
      (section) => sectionValues[Math.min(sectionValues.indexOf(section) + 1, sectionValues.length - 1)]
    );
  }, []);

  const form = useForm<DeepPartialSkipArrayKey<KeysCamelCaseDeep<CreateCustomRoleRequest>>>({
    defaultValues: {
      name: '',
      description: '',
      platformLevelPermissions: {
        achCredits: 'read',
        achDebits: 'read',
        achReturns: 'read',
        apiKeys: 'none',
        entities: 'read',
        loans: 'read',
        platformInfo: 'none',
        realtimeTransfers: 'read',
        wireTransfers: 'read',
        depositChecks: 'read',
        issueChecks: 'read',
        bookTransfers: 'read',
        stopPayments: 'read',
        counterparties: 'read',
        bankAccounts: 'read',
        reporting: 'read',
        transferTemplates: 'read',
        users: 'none',
        webhooks: 'read',
      },
    },
  });

  const handleSubmit = form.handleSubmit(
    async (data: DeepPartialSkipArrayKey<KeysCamelCaseDeep<CreateCustomRoleRequest>>) => {
      const trimmedData = trimDefaults(data);

      // Due to some limitations in the API (we can't add both sandbox and live entities/accounts in the same request,
      // we must perform two updates to 1. set the sandbox entities/accounts then 2. set the live entities/accounts.
      if (currentPlatform?.isLiveEnabled) {
        const sandboxData = buildCreateRequestForEntitiesAndAccountsSubset(trimmedData, sandboxEntitiesAndAccounts);
        await RoleRepository.create({ mode: 'sandbox', ...sandboxData })
          .then((resp) => updateRole.createRequest({ roleId: resp.id, mode: 'live', ...trimmedData }))
          .catch((err) => addDangerNotification({ content: err.message }));
      } else {
        // The hook's onError reports the failure.
        await createRole.createRequest(trimmedData).catch(() => undefined);
      }
    },
    (errors) => {
      // Role Info holds the only required field, so send the user back to fix it.
      setVisibleSection(Section.Info);
      handleSubmitErrors(errors);
    }
  );

  const { isSubmitting } = form.formState;
  const actionButton = isLastSection ? (
    <Button type="button" size="sm" onClick={handleSubmit} disabled={isSubmitting}>
      <Icon.Plus className="[--icon-size-x:16px] [--icon-size-y:16px]" />
      {isSubmitting ? 'Creating…' : 'Create Role'}
    </Button>
  ) : (
    <Button type="button" size="sm" variant="secondary" onClick={handleGoForward}>
      Next
      <Icon.ChevronRight className="[--icon-size-x:16px] [--icon-size-y:16px]" />
    </Button>
  );

  return (
    <>
      <div ref={topRef} />
      <StateTabs
        value={visibleSection}
        onValueChange={(section) => setVisibleSection(section as Section)}
        entries={sections}
      />
      <PageActions>{actionButton}</PageActions>
      <FormProvider {...form}>
        <form
          autoComplete="off"
          onSubmit={(event) => {
            // Enter steps through the sections before it creates the role.
            event.preventDefault();
            if (isLastSection) {
              void handleSubmit();
            } else {
              handleGoForward();
            }
          }}
        >
          {/* Every section stays mounted so validation still sees fields on the hidden ones. */}
          <div hidden={visibleSection !== Section.Info}>
            <RoleInfo />
          </div>
          <div hidden={visibleSection !== Section.Transfers}>
            <PermissionConfiguration
              visibleSection={PermissionConfigurationSection.Transfers}
              sandboxEntitiesAndAccounts={sandboxEntitiesAndAccounts}
              liveEntitiesAndAccounts={liveEntitiesAndAccounts}
            />
          </div>
          <div hidden={visibleSection !== Section.Additional}>
            <PermissionConfiguration
              visibleSection={PermissionConfigurationSection.Additional}
              sandboxEntitiesAndAccounts={sandboxEntitiesAndAccounts}
              liveEntitiesAndAccounts={liveEntitiesAndAccounts}
            />
          </div>
          <div className="flex justify-end px-(--page-margin) py-6">{actionButton}</div>
        </form>
      </FormProvider>
    </>
  );
};
